// Mutations for emailing POs, the vendor's acknowledgement and landed cost, spread into `mutations`
// (shared/mutations.ts). Sending itself isn't a mutation: it calls Gmail, so it's POST /api/po/:id/email
// (server/src/gmail/send.ts), which marks the PO sent through the same code as "Mark as sent".
// Rules and implementations: server/src/mutations/po-email.ts and landed-cost.ts.
import { z } from "zod";
import { landedCostChargeFields, landedCostFields } from "./landed-cost.ts";
import { create, custom, remove, update } from "./mutation-kit.ts";

const sender = z.union([z.literal(""), z.email("Enter a valid email").trim().toLowerCase()]);

export const poEmailMutations = {
  // Email ---
  /** Who POs go from for everyone ('' = the person sending). */
  "po_email_settings.update": update("po_email_settings", z.object({ default_sender_email: sender })),
  /** Who this vendor's POs go from ('' = the default). */
  "vendor.update_po_sender": update("vendor", z.object({ po_sender_email: sender })),
  /**
   * An email Gmail may or may not have sent (its answer was lost and HHL Ops couldn't look in Sent): staff checked.
   * went = it's in Sent (the PO is marked sent, if it's still what was sent); not = it isn't (failed; send again).
   */
  "po_email.resolve": custom(z.object({ id: z.uuid(), went: z.boolean() })),
  /** Lets other staff send POs from a mailbox (its owner or an admin decides). */
  "gmail_account.update": update("gmail_account", z.object({ shared: z.boolean() })),

  /** The vendor confirmed qty, price and dates: a sent PO becomes acknowledged, with their ref and ship date. */
  "po.acknowledge": custom(
    z.object({
      id: z.uuid(),
      vendor_ref: z.string().trim().max(100).optional(),
      expected_ship: z.iso.date("Use a date like 2026-12-31").nullable().optional(),
      note: z.string().trim().max(2000).optional(),
    }),
  ),

  // Landed cost ---
  /** A draft landed cost for a PO (all its receipts) or for picked receipts (one delivery, or several POs shipped together). */
  "landed_cost.create": custom(
    z.object({
      id: z.uuid(),
      po_id: z.uuid().optional(),
      receipt_ids: z.array(z.uuid()).max(200).optional(),
      forwarder: z.string().trim().max(200).optional(),
      invoice_ref: z.string().trim().max(100).optional(),
    }),
  ),
  /** scope: 'po' (every receipt of its PO) or 'receipts' (the ones in landed_cost_receipt). */
  "landed_cost.update": update("landed_cost", landedCostFields.extend({ scope: z.enum(["po", "receipts"]) })),
  /** Only a draft that was never applied. */
  "landed_cost.remove": remove("landed_cost"),
  "landed_cost_receipt.create": create("landed_cost_receipt", z.object({ landed_cost_id: z.uuid(), receipt_id: z.uuid() })),
  "landed_cost_receipt.remove": remove("landed_cost_receipt"),
  "landed_cost_charge.create": create("landed_cost_charge", landedCostChargeFields.partial().required({ landed_cost_id: true })),
  "landed_cost_charge.update": update("landed_cost_charge", landedCostChargeFields.omit({ landed_cost_id: true })),
  "landed_cost_charge.remove": remove("landed_cost_charge"),
  /** Puts each local item's share on its receipt moves (cost adjustments) and marks it applied. Applying again after a change adds the difference. */
  /** confirm_rates: a non-USD charge's USD rate of 1 is right (otherwise it's refused as a rate never entered). */
  "landed_cost.apply": custom(z.object({ id: z.uuid(), confirm_rates: z.boolean().optional() })),
  /** Takes everything it put on the ledger back off. */
  "landed_cost.void": custom(z.object({ id: z.uuid() })),
  /** An applied landed cost can change again; the ledger keeps what was applied until it's applied again. */
  "landed_cost.reopen": custom(z.object({ id: z.uuid() })),
  /** Sets Shopify unit costs (through the catalog editor's shopify.edit_variants) to the previewed weighted averages. */
  "landed_cost.update_shopify_costs": custom(
    z.object({
      id: z.uuid(),
      confirm_rates: z.boolean().optional(),
      costs: z
        .array(z.object({ item_id: z.uuid(), std_cost: z.number().nonnegative("A cost is 0 or more") }))
        .min(1, "Pick at least one item")
        .max(500),
    }),
  ),
} as const;
